/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorOutlinedIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { alpha, type Theme } from '@mui/material';
import { green, grey } from '@mui/material/colors';
import {
  type AbsoluteURL,
  FlowRunState,
  type FlowRunStatus,
  type PrefectFlow,
  type ServiceName,
} from '@poweralpha/common';
import RunningIcon from '../icons/icon-sport-runner.svg?react';
import { Emoji } from '../react-components/Emoji';

export const RUN_ENTRY_HEIGHT = 55;

export const PAGE_SIZE = 10;

export const isFlowRunning = (run: FlowRunStatus) =>
  [FlowRunState.SCHEDULED, FlowRunState.PENDING, FlowRunState.RUNNING].includes(run.state.type);

export const getRunStyling = (theme: Theme, run: FlowRunStatus) => {
  const { state } = run;
  let color: string;
  let icon: React.ReactNode;
  switch (state.type) {
    case FlowRunState.COMPLETED:
      color = theme.palette.success.main;
      icon = <CheckCircleIcon style={{ fontSize: 16, marginRight: 0, color: green[500] }} />;
      break;
    case FlowRunState.CRASHED:
    case FlowRunState.FAILED:
      color = theme.palette.error.main;
      icon = (
        <ErrorOutlinedIcon
          color="error"
          fontSize="inherit"
          style={{ fontSize: 16, marginRight: 0 }}
        />
      );
      break;
    case FlowRunState.PENDING:
    case FlowRunState.RUNNING:
      color = theme.palette.info.main;
      icon = <RunningIcon height={16} style={{ marginRight: 0 }} />;
      break;
    case FlowRunState.CANCELLING:
    case FlowRunState.CANCELLED:
      color = theme.palette.divider;
      icon = (
        <ErrorOutlinedIcon
          color="error"
          fontSize="inherit"
          style={{ fontSize: 16, marginRight: 0, color: grey[500] }}
        />
      );
      break;
    case FlowRunState.PAUSED:
    case FlowRunState.SCHEDULED:
      icon = <ScheduleIcon style={{ fontSize: 16, marginRight: 0, color: grey[500] }} />;
      color = theme.palette.divider;
      break;
    default:
      color = theme.palette.primary.main;
      icon = <Emoji symbol="❓" fontSize={12} />;
  }

  const baseSx = {
    p: 1,
    pr: 2,
    pl: 2,
    position: 'relative',
    height: RUN_ENTRY_HEIGHT,
    cursor: 'default',
  };

  return {
    baseSx,
    clickableSx: {
      ...baseSx,
      cursor: 'pointer',
      '&:hover .run-arrow': {
        opacity: 1,
      },
      '&:hover': {
        backgroundColor: alpha(color, 0.2),
      },
    },
    icon,
  };
};

export const flowNameToLogURL: Record<PrefectFlow, AbsoluteURL> = {
  'ESS only Project': '/ess/costing/logs',
  'PV only Project': '/projects',
  'PV+ESS Project': '/projects',
  'ESS Only Batch': '/projects',
  'PV Only Batch': '/projects',
  'PV+ESS Batch': '/projects',
  PlantSim: '/plant/logs',
  'PlantSim v2': '/plant-v2/logs',
  'PlantSim v2 Batch': '/plant-v2/logs',
  'PlantSim Batch': '/plant/logs',
  'PlantSim Net Benefit': '/plant/logs',
  'PlantSim v2 Net Benefit': '/plant-v2/logs',
  'PVSim weather from API': '/db/logs',
  'Solar Resource Report': '/db/logs',
  'PVSim simulate energy': '/pv/logs',
  'PVCosting only': '/pv/logs',
  'ESS Costing only, Li-ion': '/ess/costing/logs',
  'Add Price Curves': '/db/logs',
  'Energy Scoring': '/projects',
  'METSAT Fleet Report': '/db/logs',
  'Update BRWF Eng Results': '/projects', // won't be used
  'Upload Pricing Scenarios': '/projects', // status shows in the pricing upload dialog
  'BRWF Agent Vetting': '/db/logs',
  'Economic Optimum Gathering': '/db/logs',
  METCAP: '/db/logs',
  METSAT: '/db/logs',
  'METSAT PVSim Comparison': '/db/logs',
  METSOIL: '/db/logs',
};

export const serviceNameToResultURL: Record<keyof ServiceName, AbsoluteURL> = {
  pv_sim: '/pv/pvsim/result',
  pv_costing: '/pv/costing/simulate',
  ess_costing: '/ess/costing/simulate',
  plant_sim: '/plant/results',
  plant_sim_v2: '/plant-v2/results',
  plant_sim_v2_batch: '/plant-v2/batch/results',
  plant_sim_batch: '/plant/batch/results',
  plant_sim_net_benefit: '/plant/net-benefit',
  project: '/projects',
  metcap: '/db/met-stations',
};
